// Public community directory served by the Rust API. Drives the pre-auth login
// page; replaces (with fallback) the hardcoded ssoConfigurations /
// serviceConfigurations maps. Unauthenticated. Backed by the generated Rust
// client (@rubi/api-generated/clientRs), configured in api/index.ts.
import { listCommunityPublic } from "@rubi/api-generated/clientRs";
import type {
  BrandingAsset,
  CommunityBranding,
  CommunityCitizenConfig,
  CommunityPublic,
} from "@rubi/api-generated/clientRs";

import {
  type DiscoveryTarget,
  DISCOVERY_TIMEOUT_MS,
  discoveryTargets,
} from "./region";

// Single source of truth: re-export the generated types rather than duplicating them.
export type {
  BrandingAsset,
  CommunityBranding,
  CommunityCitizenConfig,
  CommunityPublic,
};

/**
 * A directory row alongside the region its own `api_region` column declared,
 * kept only long enough to break a cross-region tie in `dedupeAcrossRegions`.
 */
interface RegionRow {
  community: CommunityPublic;
  declaredRegion: string;
}

async function fetchRegionCommunities(
  target: DiscoveryTarget,
): Promise<RegionRow[]> {
  const { data } = await listCommunityPublic({
    baseURL: target.baseUrl,
    timeout: DISCOVERY_TIMEOUT_MS,
  });
  // The deployment that served the row is what its region is; a stale
  // `api_region` column in one database must not re-point the client.
  return (data ?? []).map((community) => ({
    community: { ...community, api_region: target.region },
    declaredRegion: community.api_region,
  }));
}

/**
 * Identity keys a consumer might collapse this directory on: the org UUID, the
 * title (this app's key, in `useCommunities`), and the services slug (the
 * citizen app's). A row matching an earlier region on any one of them is the
 * same agency seen twice. All three are needed — some orgs were bootstrapped
 * into both databases independently and their UUIDs drifted apart, while others
 * share a UUID but were renamed in only one of them.
 */
function identityKeys(community: CommunityPublic): string[] {
  // Only keys the row actually carries: an absent field is not an identity two
  // rows can share, and treating it as one would collapse the directory.
  return [
    community.id && `id:${community.id}`,
    community.title && `title:${community.title}`,
    community.services_url_slug && `slug:${community.services_url_slug}`,
  ].filter((key): key is string => Boolean(key));
}

/**
 * One row per agency, from the region that owns it.
 *
 * Under separation an org exists in exactly one database and this is a no-op.
 * It is not one yet: both deployments still carry nearly the same org set, so
 * the directory arrives holding two rows for most agencies — one stamped `ca`,
 * one stamped `us`. `useCommunities` collapses the list into maps keyed by
 * title with a plain assignment, so without this the last region asked silently
 * wins: every duplicated agency takes its region, SSO slug, provider and crest
 * from the other deployment's copy.
 *
 * The tie is broken on the `api_region` column, the only thing that
 * distinguishes two copies of one row: whichever database says the org is its
 * own keeps it. This is not the column re-pointing the client — the surviving
 * row is still stamped with the deployment that served it, so a region can only
 * win a tie for a row it actually holds. It decides which of two rows that both
 * genuinely exist is the real one, which is a question the serving region alone
 * cannot answer.
 *
 * When neither copy claims the org (or both do), the first region asked wins,
 * which `API_REGIONS` makes the default region.
 */
function dedupeAcrossRegions(rows: RegionRow[]): CommunityPublic[] {
  const indexByKey = new Map<string, number>();
  const kept: RegionRow[] = [];
  const conflicts: { agency: string; keeping: string; dropping: string }[] = [];

  const claim = (row: RegionRow, index: number) => {
    for (const key of identityKeys(row.community)) {
      indexByKey.set(key, index);
    }
  };
  const ownsItself = (row: RegionRow) =>
    row.declaredRegion === row.community.api_region;

  for (const row of rows) {
    const index = identityKeys(row.community)
      .map((key) => indexByKey.get(key))
      .find((found) => found !== undefined);

    if (index === undefined) {
      claim(row, kept.length);
      kept.push(row);
      continue;
    }

    const incumbent = kept[index];
    const replace = ownsItself(row) && !ownsItself(incumbent);
    conflicts.push({
      agency: incumbent.community.title,
      keeping: (replace ? row : incumbent).community.api_region,
      dropping: (replace ? incumbent : row).community.api_region,
    });

    if (replace) {
      kept[index] = row;
      // The winner may key differently from the row it replaces (a rename lands
      // in one database before the other), so both sets of keys have to resolve
      // to this slot or the loser reappears under its old name.
      claim(row, index);
    }
  }

  if (conflicts.length > 0) {
    // Slug uniqueness is enforced per-database and checked across regions at
    // onboarding, so a duplicate means the two databases still overlap rather
    // than that onboarding let one through. Logged once with the whole set:
    // while the overlap is this wide, a line per agency buries everything else.
    console.error(
      `fetchCommunities: ${conflicts.length} agencies are held by more than one region`,
      conflicts,
    );
  }

  return kept.map((row) => row.community);
}

export interface CommunityDirectory {
  communities: CommunityPublic[];
  /** False when a region failed, so an agency missing from the list may still exist. */
  complete: boolean;
}

/**
 * Every community, from every region.
 *
 * Each deployment holds only its own orgs, so an officer's agency is only in
 * this list if the region holding it is asked. A region that fails degrades to
 * the regions that answered rather than emptying the picker.
 */
export async function fetchCommunityDirectory(): Promise<CommunityDirectory> {
  const targets = discoveryTargets();
  const results = await Promise.allSettled(
    targets.map((target) => fetchRegionCommunities(target)),
  );

  const rows: RegionRow[] = [];
  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      rows.push(...result.value);
      return;
    }
    console.error(
      `fetchCommunities: the ${targets[index].region} directory is unavailable`,
      result.reason,
    );
  });
  return {
    communities: dedupeAcrossRegions(rows),
    complete: results.every((result) => result.status === "fulfilled"),
  };
}

export async function fetchCommunities(): Promise<CommunityPublic[]> {
  return (await fetchCommunityDirectory()).communities;
}
